import { defineMessages, type MessageDescriptor } from 'react-intl';

type MessageKey =
	| 'singleAppTitle'
	| 'singleAppBody'
	| 'multiAppPriceIncreaseTitle'
	| 'multiAppPriceIncreaseBody'
	| 'priceSavingTitle'
	| 'priceSavingBody'
	| 'moreAiCredits'
	| 'cta'
	| 'modalLabel'
	| 'modalLoading';

export const messages: Record<MessageKey, MessageDescriptor> = defineMessages({
	singleAppTitle: {
		id: 'post-office.adminhub-twc-discovery.single-app.title',
		defaultMessage: 'Get {benefits} with your {ownedApp} plan',
		description:
			'Banner title for an Admin Hub org paying for one app. {benefits} is a list of the apps they would gain plus "10x more AI credits"; {ownedApp} is the app they already pay for, e.g. Jira',
	},
	singleAppBody: {
		id: 'post-office.adminhub-twc-discovery.single-app.body',
		defaultMessage:
			'One bundled subscription. Switch back anytime. Try them together for free with Teamwork Collection.',
		description:
			'Banner body for an Admin Hub org paying for one app, promoting a free trial of the Teamwork Collection bundle',
	},
	multiAppPriceIncreaseTitle: {
		id: 'post-office.adminhub-twc-discovery.multi-app-price-increase.title',
		defaultMessage:
			'{edition, select, standard {Try three apps together for free with Teamwork Collection} other {Try four apps together for free with Teamwork Collection}}',
		description:
			'Banner title for an Admin Hub org paying for two apps, promoting a free trial of the Teamwork Collection bundle. {edition} is "standard" (three apps) or "premium" (four apps, including Guard)',
	},
	multiAppPriceIncreaseBody: {
		id: 'post-office.adminhub-twc-discovery.multi-app-price-increase.body',
		defaultMessage:
			'Keep {ownedApps}. Get {benefits}. One bundled subscription. Switch back anytime.',
		description:
			'Banner body for an Admin Hub org paying for several apps. {ownedApps} is a list of the apps they already pay for, e.g. "Jira and Confluence"; {benefits} is a list of the apps they would gain plus "10x more AI credits"',
	},
	priceSavingTitle: {
		id: 'post-office.adminhub-twc-discovery.price-saving.title',
		defaultMessage:
			'{edition, select, standard {Pay less for three apps than you’re paying for {ownedApps}} other {Pay less for four apps than you’re paying for {ownedApps}}}',
		description:
			'Banner title telling an Admin Hub org that Teamwork Collection costs less than the apps they pay for. {ownedApps} is a list of those apps, e.g. "Jira and Confluence"; {edition} is "standard" (three apps) or "premium" (four apps, including Guard)',
	},
	priceSavingBody: {
		id: 'post-office.adminhub-twc-discovery.price-saving.body',
		defaultMessage:
			'Get {benefits} at no extra cost. One bundled subscription. Switch back anytime.',
		description:
			'Banner body for the Teamwork Collection price saving banner. {benefits} is a list of the apps the org would gain plus "10x more AI credits"',
	},
	moreAiCredits: {
		id: 'post-office.adminhub-twc-discovery.more-ai-credits',
		defaultMessage: '10x more AI credits',
		description:
			'Last item in the list of benefits an Admin Hub org gets with Teamwork Collection, after the app names, e.g. "Confluence, Loom, and 10x more AI credits"',
	},
	cta: {
		id: 'post-office.adminhub-twc-discovery.cta',
		defaultMessage: 'Explore Teamwork Collection',
		description: 'Button on the Teamwork Collection banner in Admin Hub billing preview',
	},
	modalLabel: {
		id: 'post-office.adminhub-twc-discovery.modal.label',
		defaultMessage: 'Teamwork Collection plan comparison',
		description:
			'Accessible name for the Teamwork Collection comparison modal and its embedded page, opened from the Admin Hub billing preview banner',
	},
	modalLoading: {
		id: 'post-office.adminhub-twc-discovery.modal.loading',
		defaultMessage: 'Loading Teamwork Collection plan comparison',
		description:
			'Accessible label for the spinner shown while the Teamwork Collection comparison modal loads its content',
	},
});
